class GoldFabricsCarousel extends HTMLElement {
  static pending = new Set();
  static frame = null;

  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.previous = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.controls = this.querySelector('[data-carousel-controls]');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-index]'));
    this.slides = this.dataset.hero === 'true' ? Array.from(this.track.children) : [];
    this.previous?.addEventListener('click', () => this.move(-1), { signal });
    this.next?.addEventListener('click', () => this.move(1), { signal });
    this.dots.forEach((button) => {
      button.addEventListener('click', () => this.goTo(Number(button.dataset.carouselIndex)), { signal });
    });
    this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
    this.observer = new ResizeObserver(() => this.scheduleUpdate());
    this.observer.observe(this.track);
    this.viewportActive = true;
    if (this.dataset.hero !== 'true' && matchMedia('(max-width: 749px)').matches && 'IntersectionObserver' in window) {
      this.viewportActive = false;
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.viewportActive = entry.isIntersecting;
        if (this.viewportActive) this.scheduleUpdate();
      }, { rootMargin: '400px 0px' });
      this.visibilityObserver.observe(this);
    }
    this.scheduleUpdate();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.visibilityObserver?.disconnect();
    GoldFabricsCarousel.pending.delete(this);
  }

  scheduleUpdate() {
    if (!this.viewportActive) return;
    GoldFabricsCarousel.pending.add(this);
    if (GoldFabricsCarousel.frame !== null) return;
    GoldFabricsCarousel.frame = requestAnimationFrame(() => {
      GoldFabricsCarousel.frame = null;
      const carousels = Array.from(GoldFabricsCarousel.pending).filter((carousel) => carousel.isConnected);
      GoldFabricsCarousel.pending.clear();
      // Read every carousel before changing controls or slide accessibility.
      const measurements = carousels.map((carousel) => {
        const width = carousel.track.clientWidth;
        const position = Math.abs(carousel.track.scrollLeft);
        return { carousel, max: carousel.track.scrollWidth - width, position, index: width ? Math.round(position / width) : 0 };
      });
      measurements.forEach(({ carousel, ...state }) => carousel.update(state));
    });
  }

  move(direction) {
    const sign = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({ left: direction * sign * this.track.clientWidth, behavior: this.behavior });
  }

  goTo(index) {
    const item = this.track.children[index];
    if (item) this.track.scrollBy({ left: item.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: this.behavior });
  }

  get behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }

  update({ max, position, index }) {
    if (this.previous && this.previous.disabled !== (position <= 2)) this.previous.disabled = position <= 2;
    if (this.next && this.next.disabled !== (position >= max - 2)) this.next.disabled = position >= max - 2;
    if (this.controls && this.controls.hidden !== (max <= 2)) this.controls.hidden = max <= 2;
    this.slides.forEach((slide, i) => { if (slide.inert !== (i !== index)) slide.inert = i !== index; });
    this.dots.forEach((button, i) => {
      const pressed = String(i === index);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
  }
}

if (!customElements.get('gf-carousel')) customElements.define('gf-carousel', GoldFabricsCarousel);
